import React, { useState } from "react";
import { Box, CardHeader, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import { canCloseWorkOrder } from "../WorkOrder/utils";
import CloseWorkOrderModal from "../WorkOrder/Components/CloseWorkOrderModal";

import { ButtonWithIcon, DataGrid, PageTitleWithToolTip } from "../Common";
import { BadgeWithLinkedText } from '../Common/DataGrid/CellComponents';
import { text_commonCellProps } from '../Common/DataGrid/utils';
import { getDateRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import { Grey2 } from '../../color_utils';
import Routes from "../../routes";
import UserInfo from "../../UserInfo";
import useNavigate from "../../hooks/useNavigate";
import { TranslateW } from "../../Utils/Languages/Translate";

const dateRangeFilterOperator = getDateRangeFilterOperator({});

const columns = [
    {
        field: 'id',
        headerName: 'Work order ID',
        ...text_commonCellProps,
        renderCell: ({ row: { status, id, isDeleted } }) => (
            <BadgeWithLinkedText
                statusProperty={isDeleted ? status : 'deleted'}
                text={id}
                to={{
                    pathname: `/WorkOrder/${id}`,
                    state: {
                        from: window.location.pathname
                    }
                }}
            />
        )
    },
    {
        field: 'locationName',
        headerName: 'Location',
        ...text_commonCellProps,
    },
    {
        field: 'workOrderNumber',
        headerName: 'Customer work order #',
        ...text_commonCellProps,
    },
    {
        field: 'scheduledDate',
        headerName: 'Scheduled Date',
        filterOperators: dateRangeFilterOperator,
    },
    {
        field: 'statusReason',
        headerName: 'Status reason',
        ...text_commonCellProps,
    },
    {
        field: 'servicer',
        headerName: 'Servicer provider',
        ...text_commonCellProps,
    },
    {
        field: 'type',
        headerName: 'Work type',
        ...text_commonCellProps,
    },
]

const WorkOrderTable = ({ id }) => {
    const [actionModal, setActionModal] = useState(null);
    const [modalPayload, setModalPayload] = useState({});
    const [refreshNeeded, setRefreshNeeded] = useState(false);

    const navigate = useNavigate({
        pageName: "WorkOrderTable"
    });

    const openModal = (workOrderData, modalType) => {
        const payload = {
            ...workOrderData,
            ID: workOrderData.id,
        };

        setModalPayload(payload);
        setActionModal(modalType);
    };

    const actionRow = {
        type: 'actions',
        field: 'Quick Actions',
        width: 140,
        align: 'left',
        getActions: ({ row }) => {

            const { id } = row;
            const quickActions = [];

            if (!UserInfo.isReadOnly()) {
                quickActions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip
                                title="Edit Work Order"
                            >
                                <CreateIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/EditWorkOrder/${id}`)}
                    />
                );
            }

            if (!UserInfo.isReadOnly() && row.status && row.status.toLowerCase() !== "closed") {
                quickActions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip
                                title="Create Service Event"
                            >
                                <AddIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/CreateServiceEvent/${id}`)}
                    />
                );
            }

            if (canCloseWorkOrder(row)) {
                quickActions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip
                                title="Close Work Order"
                            >
                                <CloseIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => openModal(row, "Close")}
                    />
                );
            }

            return quickActions;
        }
    }

    return (
        <>
            <DataGrid
                hideExportButton
                url={Routes.Location.WorkOrderTable(id)}
                noResultText="Oops...It is empty here..."
                noResultText2="Create a new Work Order or change your search criterias."
                refresh={refreshNeeded}
                columns={[...columns, actionRow]}
                headerContent={
                    <CardHeader
                        title={
                            <PageTitleWithToolTip
                                pageTitle={TranslateW("assetdetailsmaintab.workOrder")}
                            />
                        }
                        sx={{
                            "& .MuiCardHeader-action": { display: "flex" },
                            "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                        }}
                    />
                }
            />
            <CloseWorkOrderModal
                payload={modalPayload}
                modalOpen={actionModal === "Close"}
                onClose={() => setActionModal(null)}
                refreshRequired={() => setRefreshNeeded(!refreshNeeded)}
            />
        </>
    );
};

export default WorkOrderTable;
